import { useState, useRef, useEffect } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";

// FAQ data - replace with your actual questions and answers
const faqData = [
  {
    question: "What products do you sell?",
    answer: (
      <>
        We specialize in items essential for medical students and professionals:
        <ul className="list-disc pl-5 mt-2">
          <li>Stethoscopes (Littmann – Classic III, Cardiology IV)</li>
          <li>Flexible Knee Hammers</li>
          <li>Medical Pen Torches</li>
          <li>Stethoscope Cases</li>
          <li>Samsung Tablets (S6, S7, S7+, S8, S8+)</li>
          <li>Tablet Accessories</li>
        </ul>
      </>
    ),
  },
  {
    question: "Are your stethoscopes original?",
    answer: (
      <>
        Yes! All Littmann stethoscopes are <strong>100% genuine</strong>, directly imported from the USA.
        <br />
        We do <strong>not</strong> deal in fakes or third-party country imports.
      </>
    ),
  },
  {
    question: "What warranty do you offer for stethoscopes?",
    answer: (
      <>
        We provide official Littmann warranty:
        <ul className="list-disc pl-5 mt-2">
          <li><strong>Classic III</strong> – 5 Years</li>
          <li><strong>Cardiology IV</strong> – 7 Years</li>
        </ul>
        <br />
        We also offer a <strong>temporary replacement</strong> stethoscope during repairs — a service no other Sri Lankan seller provides.
        <br /><br />
        <strong>Note:</strong> Warranty does not cover misuse such as liquid immersion, sterilization, or physical damage.
      </>
    ),
  },
  {
    question: "Why should I buy Littmann from you?",
    answer: (
      <ul className="list-disc pl-5">
        <li>Only latest 2025-manufactured stock</li>
        <li>No fake or outdated items</li>
        <li>Direct imports from USA</li>
        <li>Best after-sales support in Sri Lanka</li>
        <li>Laser engraving by Littmann USA – warranty-safe</li>
      </ul>
    ),
  },
  {
    question: "Are your accessories hospital-approved?",
    answer: (
      <>
        Absolutely. We sell <strong>real ward-used</strong> flexible knee hammers and other tools.
        <br />
        No cheap China-made imitations – our hammers are imported from Pakistan, used and approved by consultants.
      </>
    ),
  },
  {
    question: "What's the condition of the Samsung tablets you sell?",
    answer: (
      <>
        We import tablets in top condition from Singapore, in three grades:
        <br /><br />
        <strong>A+ Grade:</strong>
        <ul className="list-disc pl-5 mt-1">
          <li>Under 7 months in showroom</li>
          <li>Like new, no scratches</li>
          <li>Battery health over 95%</li>
        </ul>
        <br />
        <strong>A Grade:</strong>
        <ul className="list-disc pl-5 mt-1">
          <li>Over 1 year in showroom</li>
          <li>Minor scratches</li>
          <li>Battery health 85–95%</li>
        </ul>
        <br />
        <strong>A- Grade (Rarely sold):</strong>
        <ul className="list-disc pl-5 mt-1">
          <li>Over 2 years in showroom</li>
          <li>Noticeable scratches</li>
          <li>Battery health 80–85%</li>
        </ul>
        <br />
        We mainly stock <strong>A+</strong> and <strong>A grade</strong> only.
      </>
    ),
  },
  {
    question: "What tablet models do you offer?",
    answer: (
      <>
        We are the only Sri Lankan retailer selling:
        <ul className="list-disc pl-5 mt-2">
          <li>Samsung Galaxy Tab S6</li>
          <li>Samsung Galaxy Tab S7 / S7+</li>
          <li>Samsung Galaxy Tab S8 / S8+</li>
        </ul>
      </>
    ),
  },
  {
    question: "Do you provide a warranty for tablets?",
    answer: (
      <>
        Yes.
        <ul className="list-disc pl-5 mt-2">
          <li>1 Month checking warranty</li>
          <li>1 Year software warranty</li>
        </ul>
      </>
    ),
  },
  {
    question: "Is Cash on Delivery (COD) available?",
    answer: (
      <>
        Yes! We offer <strong>Cash on Delivery</strong>.
        <br />
        You only pay after receiving your parcel.
      </>
    ),
  },
];

// FAQ Item Component with dynamic height calculation
const FAQItem = ({ question, answer, isOpen, onClick }) => {
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(isOpen ? contentRef.current.scrollHeight : 0);
    }
  }, [isOpen]);

  return (
    <div className="border-b border-_gray_lite">
      <button
        className="flex justify-between items-center w-full py-4 px-6 text-left focus:outline-none"
        onClick={onClick}
        aria-expanded={isOpen}
      >
        <span className="font-medium text-_dark md:text-lg">{question}</span>
        <span className="text-_blue transition-transform duration-300">
          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </span>
      </button>

      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          height: `${contentHeight}px`,
          opacity: isOpen ? 1 : 0,
          transition: "height 0.3s ease-in-out, opacity 0.3s ease-in-out",
        }}
      >
        <div ref={contentRef} className="pb-5 px-6 text-_dark_lite">
          <div>{answer}</div>
        </div>
      </div>
    </div>
  );
};

// Main FAQ Component
const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="w-full h-full bg-_white max-w-4xl mx-auto my-12 px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-10">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-_dark mb-2">
          Frequently Asked Questions
        </h2>
        <p className="text-_dark_lite max-w-2xl mx-auto">
          Find answers to the most common questions about our services and
          platform.
        </p>
      </div>

      <div className="shadow-lg rounded-lg overflow-hidden bg-white">
        {faqData.map((faq, index) => (
          <FAQItem
            key={index}
            question={faq.question}
            answer={faq.answer}
            isOpen={openIndex === index}
            onClick={() => toggleFAQ(index)}
          />
        ))}
      </div>

      <div className="text-center mt-8">
        <p className="text-_dark_lite mb-4">
          Couldn't find what you're looking for?
        </p>
        <button className="bg-_blue hover:bg-_bg_dark text-_white font-medium py-2 px-6 rounded-md transition-colors duration-300">
          <Link to="/contact">Contact Support</Link>
        </button>
      </div>
    </div>
  );
};

export default FAQ;